'use client';

import { CDN_BASE_URL } from '@/lib/constants/cdn';
import { cn } from '@/lib/utils';
import { debounce } from 'lodash';
import Image from 'next/image';
import { useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TextGenerateEffectProps {
  words?: string;
  className?: string;
  loading?: boolean;
  adviceStatus?: string;
  shouldAnimate?: boolean;
  isNew?: boolean;
  isStreaming?: boolean;
  isLastAdvice?: boolean;
  showPlaceholder?: boolean;
  contentId?: string;
}

export const TextGenerateEffect = ({
  words,
  className,
  loading,
  adviceStatus,
  isLastAdvice,
  shouldAnimate = false,
  isNew = true,
  isStreaming = false,
  showPlaceholder = true,
  contentId,
}: TextGenerateEffectProps) => {
  const messageContainerRef = useRef<HTMLDivElement>(null);

  // Detect error status directly from props or content
  const isErrorResponse = useMemo(() => {
    return (
      adviceStatus === 'error' ||
      (words &&
        typeof words === 'string' &&
        (words.startsWith('Sorry, your question') ||
          words.includes("isn't about Quebec housing law")))
    );
  }, [adviceStatus, words]);

  // Simplified content handling
  const contentToDisplay = useMemo(() => {
    // For error responses, always use the direct content
    if (isErrorResponse) {
      return typeof words === 'string' ? words : String(words || '');
    }

    // For normal content, use words directly - streaming API already provides proper markdown
    if (words && typeof words === 'string') {
      return words;
    }

    // Fallback for empty content
    return showPlaceholder ? 'No response content available.' : '';
  }, [isErrorResponse, words, showPlaceholder]);

  // Simple auto-scroll to bottom
  const scrollToBottom = useMemo(
    () =>
      debounce(() => {
        if (messageContainerRef.current) {
          const isNearBottom =
            window.innerHeight + window.scrollY >
            document.body.offsetHeight - 200;

          if (isNearBottom || isStreaming) {
            messageContainerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            });
          }
        }
      }, 100),
    [isStreaming]
  );

  // Scroll down when content changes
  useEffect(() => {
    if (contentToDisplay && contentToDisplay.length > 20) {
      scrollToBottom();
    }
  }, [contentToDisplay, scrollToBottom]);

  // Render the markdown content
  const renderedContent = useMemo(
    () => (
      <div className='flex w-full items-start space-x-2 sm:space-x-3 md:space-x-4'>
        <div className='hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:flex sm:h-12 sm:w-12 md:h-14 md:w-14'>
          <div className='relative h-[30px] w-[30px] sm:h-[40px] sm:w-[40px] md:h-[60px] md:w-[60px]'>
            <Image
              src={`${CDN_BASE_URL}/JAQLNLogo.png`}
              alt='Jaqln avatar'
              fill
              priority
              sizes='(max-width: 640px) 30px, (max-width: 768px) 40px, 60px'
              className='object-contain'
            />
          </div>
        </div>
        <div className='prose prose-sm mt-1 max-w-none flex-1 text-wrap pb-3 pr-2 text-gray-800 dark:prose-invert dark:text-gray-100 sm:mt-2 sm:pr-4 md:pr-8'>
          <div className='relative w-full'>
            <div className='prose prose-neutral w-full max-w-prose dark:prose-invert'>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {contentToDisplay}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    ),
    [contentToDisplay]
  );

  return (
    <div className={cn('flex flex-col', className)} ref={messageContainerRef}>
      {renderedContent}
    </div>
  );
};
